import {
  Button,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetNegotiationWithoutNoticeQuery } from "../../../../../autogen/bff-api";
import { ListLoadError } from "../../../../../common/ListLoadError";

interface Props {
  eventId: string;
  onClose: () => void;
  onConfirmed: () => Promise<void> | void;
}

export const OpenWithoutNoticeModal = ({ eventId, onClose, onConfirmed }: Props) => {
  const { t } = useTranslation();
  const { data, isFetching, isError, refetch } = useGetNegotiationWithoutNoticeQuery(
    { eventId },
    { refetchOnMountOrArgChange: true },
  );
  const [isOpening, setIsOpening] = useState(false);
  const invitees = data?.invitees ?? [];
  const canOpen = !isFetching && !isError && invitees.length > 0;

  const open = async () => {
    setIsOpening(true);
    try {
      await onConfirmed();
      onClose();
    } finally {
      setIsOpening(false);
    }
  };

  return (
    <Modal isOpen onClose={isOpening ? () => undefined : onClose} size="3xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("Open the competition for tenders?")}</ModalHeader>
        <ModalCloseButton isDisabled={isOpening} />
        <ModalBody>
          {isFetching ? (
            <Spinner size="sm" />
          ) : isError ? (
            <ListLoadError message={t("Could not load the suppliers to be invited.")} onRetry={refetch} />
          ) : (
            <>
              <Text fontSize="sm" fontWeight="semibold">
                {t("Invited when you open the competition ({{count}})", { count: invitees.length })}
              </Text>
              <UnorderedList fontSize="sm" mt="2" spacing="0.5">
                {invitees.map((supplier) => (
                  <ListItem key={supplier.organizationId}>{supplier.name}</ListItem>
                ))}
              </UnorderedList>
            </>
          )}
          <Text mt="4" fontSize="sm" color="smMuted">
            {t("The invitations are sent at once, and the list of invited suppliers is fixed. Nothing is sent to Doffin.")}
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isOpening}>
            {t("Cancel")}
          </Button>
          <Button variant="outline" colorScheme="teal" onClick={open} isLoading={isOpening} isDisabled={!canOpen}>
            {t("Open for tenders")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
